"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Calendar, Users, Star, Search, StickyNote } from "lucide-react"
import type { Fencer } from "../types"
import { api } from "../services/api"
import { formatDate, calculateAge, isAdult } from "../utils/dateUtils"
import { Switch } from "../components/Switch"

interface FencerAttendance {
  fencer: Fencer
  present: boolean
  privateLessonReceived: boolean
  notes: string
  recordId?: string
}

export const AttendancePage: React.FC = () => {
  const [fencers, setFencers] = useState<Fencer[]>([])
  const [filteredFencers, setFilteredFencers] = useState<Fencer[]>([])
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()))
  const [attendance, setAttendance] = useState<FencerAttendance[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [selectedDate])

  useEffect(() => {
    // Filter fencers based on search term
    const filtered = fencers.filter(
      (fencer) =>
        fencer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fencer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fencer.guardians.some((guardian) => guardian.name.toLowerCase().includes(searchTerm.toLowerCase())),
    )
    setFilteredFencers(filtered)

    // Update attendance to only show filtered fencers
    setAttendance((prev) => prev.filter((item) => filtered.some((f) => f.id === item.fencer.id)))
  }, [fencers, searchTerm])

  const loadData = async () => {
    try {
      const [fencersData, attendanceData] = await Promise.all([
        api.getFencers(),
        api.getAttendanceRecords({
          start: selectedDate,
          end: selectedDate,
        }),
      ])

      setFencers(fencersData)

      // Create attendance map for each fencer
      const attendanceMap: FencerAttendance[] = fencersData.map((fencer) => {
        const record = attendanceData.find((record) => record.fencerId === fencer.id && record.date === selectedDate)

        return {
          fencer,
          present: record ? record.present : false,
          privateLessonReceived: record ? record.privateLessonReceived : false,
          notes: record ? record.notes || "" : "",
          recordId: record?.id,
        }
      })

      setAttendance(attendanceMap)
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAttendanceChange = async (fencerId: string, field: keyof FencerAttendance, value: any) => {
    try {
      const currentRecord = attendance.find((item) => item.fencer.id === fencerId)
      if (!currentRecord) return

      const updatedRecord = { ...currentRecord, [field]: value }

      // If setting present to false, also set private lesson to false
      if (field === "present" && !value) {
        updatedRecord.privateLessonReceived = false
      }

      if (currentRecord.recordId) {
        // Update existing record
        await api.updateAttendanceRecord(currentRecord.recordId, {
          present: updatedRecord.present,
          privateLessonReceived: updatedRecord.privateLessonReceived,
          notes: updatedRecord.notes,
        })
      } else {
        // Create new record
        const newRecord = await api.addAttendanceRecord({
          fencerId,
          date: selectedDate,
          present: updatedRecord.present,
          privateLessonReceived: updatedRecord.privateLessonReceived,
          notes: updatedRecord.notes,
        })
        updatedRecord.recordId = newRecord.id
      }

      // Update local state
      setAttendance((prev) => prev.map((item) => (item.fencer.id === fencerId ? updatedRecord : item)))
    } catch (error) {
      console.error("Error updating attendance:", error)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading attendance data...</span>
      </div>
    )
  }

  const presentCount = attendance.filter((item) => item.present).length
  const privateLessonCount = attendance.filter((item) => item.privateLessonReceived).length
  const displayedAttendance = attendance.filter((item) => filteredFencers.some((f) => f.id === item.fencer.id))

  return (
    <div className="animate-fade-in">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Attendance Tracking</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Mark attendance and track private lessons for training sessions
          </p>
        </div>
      </div>

      <div className="mt-6 card p-4 sm:p-6 animate-slide-down">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Training Date
            </label>
            <div className="relative">
              <input
                type="date"
                id="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 pr-10 transition-all duration-200"
              />
              <Calendar className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            </div>
          </div>

          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter Fencers
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                placeholder="Search by name, email, or parent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 pr-10 transition-all duration-200"
              />
              <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <Users className="h-4 w-4 text-blue-500" />
            <span>Present: {presentCount}</span>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <Star className="h-4 w-4 text-yellow-500" />
            <span>Private Lessons: {privateLessonCount}</span>
          </div>

          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
            <div className="h-4 w-4 bg-green-500 rounded-full"></div>
            <span>Total Fencers: {filteredFencers.length}</span>
          </div>

          {searchTerm && (
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
              <Search className="h-4 w-4 text-gray-500" />
              <span>Filtered: {displayedAttendance.length}</span>
            </div>
          )}
        </div>

        <div className="card overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Fencer
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Present
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Private Lesson
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {displayedAttendance.map(({ fencer, present, privateLessonReceived, notes }) => {
                  const age = calculateAge(fencer.dateOfBirth)
                  const adult = isAdult(fencer.dateOfBirth)

                  return (
                    <tr
                      key={fencer.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{fencer.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-300">{fencer.email}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-400">
                            Age: {age}{" "}
                            {!adult && fencer.guardians.length > 0 && `• Parent: ${fencer.guardians[0].name}`}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Switch
                          checked={present}
                          onChange={(checked) => handleAttendanceChange(fencer.id, "present", checked)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <Switch
                          checked={privateLessonReceived}
                          onChange={(checked) => handleAttendanceChange(fencer.id, "privateLessonReceived", checked)}
                          disabled={!present}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="relative">
                          <textarea
                            value={notes}
                            onChange={(e) => handleAttendanceChange(fencer.id, "notes", e.target.value)}
                            placeholder="Add notes for this session..."
                            rows={2}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-2 text-sm resize-none transition-all duration-200"
                          />
                          {notes && (
                            <StickyNote className="absolute top-2 right-2 h-4 w-4 text-blue-500 dark:text-blue-400" />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Compact Layout */}
          <div className="md:hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-2 py-2 border-b border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-6 gap-1 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wide">
                <div className="col-span-3">Fencer</div>
                <div className="text-center">Present</div>
                <div className="text-center">Lesson</div>
                <div className="text-center">
                  <StickyNote className="h-3 w-3 mx-auto" />
                </div>
              </div>
            </div>
            {displayedAttendance.map(({ fencer, present, privateLessonReceived, notes }) => {
              const age = calculateAge(fencer.dateOfBirth)
              const adult = isAdult(fencer.dateOfBirth)

              return (
                <div
                  key={fencer.id}
                  className="px-2 py-2 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <div className="grid grid-cols-6 gap-1 items-center">
                    <div className="col-span-3 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{fencer.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {age}y • {adult ? "Adult" : "Minor"}
                      </div>
                    </div>
                    <div className="text-center">
                      <Switch
                        checked={present}
                        onChange={(checked) => handleAttendanceChange(fencer.id, "present", checked)}
                        size="sm"
                      />
                    </div>
                    <div className="text-center">
                      <Switch
                        checked={privateLessonReceived}
                        onChange={(checked) => handleAttendanceChange(fencer.id, "privateLessonReceived", checked)}
                        disabled={!present}
                        size="sm"
                      />
                    </div>
                    <div className="text-center">
                      <button
                        onClick={() => {
                          const newNotes = prompt("Enter notes for this session:", notes)
                          if (newNotes !== null) {
                            handleAttendanceChange(fencer.id, "notes", newNotes)
                          }
                        }}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                        title="Add notes"
                      >
                        <StickyNote
                          className={`h-4 w-4 ${
                            notes ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {displayedAttendance.length === 0 && !searchTerm && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No fencers registered</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add some fencers first to track their attendance.
              </p>
            </div>
          )}
          {displayedAttendance.length === 0 && searchTerm && (
            <div className="text-center py-12">
              <Search className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No fencers match your search</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search terms.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
